import React, { useState, useEffect } from 'react';
import { FiCheck, FiChevronLeft, FiPhone, FiUser, FiX } from 'react-icons/fi';
import './PropertyOwnerNamePanel.css';

/**
 * Staff-only (Admin/Partner) owner name + number capture.
 * Optional — modal on open; Submit saves and closes; Close discards draft.
 * When a value is set, a fixed top-right chip shows it; otherwise a right-edge arrow.
 */
const PropertyOwnerNamePanel = ({ name = '', number = '', onChange }) => {
  const [open, setOpen] = useState(true);
  const [draftName, setDraftName] = useState(name || '');
  const [draftNumber, setDraftNumber] = useState(number || '');

  useEffect(() => {
    setDraftName(name || '');
  }, [name]);

  useEffect(() => {
    setDraftNumber(number || '');
  }, [number]);

  const trimmedName = String(name || '').trim();
  const trimmedNumber = String(number || '').trim();
  const hasValue = !!(trimmedName || trimmedNumber);
  const chipLabel = trimmedNumber
    ? (trimmedName ? `${trimmedName} · ${trimmedNumber}` : trimmedNumber)
    : trimmedName;

  const handleSubmit = () => {
    onChange?.({
      name: String(draftName || '').trim(),
      number: String(draftNumber || '').trim(),
    });
    setOpen(false);
  };

  const handleClose = () => {
    setDraftName(name || '');
    setDraftNumber(number || '');
    setOpen(false);
  };

  const handleOpen = () => {
    setDraftName(name || '');
    setDraftNumber(number || '');
    setOpen(true);
  };

  return (
    <>
      {open && (
        <div className="pon-overlay" role="dialog" aria-modal="true" aria-labelledby="pon-title">
          <div className="pon-modal">
            <div className="pon-modal__icon">
              <FiUser size={26} />
            </div>
            <h2 id="pon-title" className="pon-modal__title">Property Owner/Reference Details</h2>
            <p className="pon-modal__hint">Optional — for internal staff use only. Never shown to users.</p>

            <label className="pon-modal__label" htmlFor="pon-name">
              Owner name
            </label>
            <input
              id="pon-name"
              className="pon-modal__input"
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Enter owner name"
              autoFocus
            />

            <label className="pon-modal__label" htmlFor="pon-number" style={{ marginTop: '0.85rem' }}>
              Owner number
            </label>
            <input
              id="pon-number"
              className="pon-modal__input"
              type="tel"
              inputMode="numeric"
              value={draftNumber}
              onChange={(e) => setDraftNumber(e.target.value.replace(/[^\d+\s-]/g, ''))}
              placeholder="Enter owner phone number"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />

            <div className="pon-modal__actions">
              <button type="button" className="pon-modal__close" onClick={handleClose}>
                <FiX size={16} /> Close
              </button>
              <button type="button" className="pon-modal__submit" onClick={handleSubmit}>
                <FiCheck size={16} /> Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {!open && hasValue && (
        <button
          type="button"
          className="pon-fixed-chip"
          onClick={handleOpen}
          title="Edit Property Owner/Reference Details"
        >
          {trimmedNumber ? <FiPhone size={14} /> : <FiUser size={14} />}
          <span>{chipLabel}</span>
        </button>
      )}

      {!open && !hasValue && (
        <button
          type="button"
          className="pon-edge-arrow"
          onClick={handleOpen}
          title="Property Owner/Reference Details"
          aria-label="Open Property Owner/Reference Details"
        >
          <FiChevronLeft size={22} />
        </button>
      )}
    </>
  );
};

export default PropertyOwnerNamePanel;
